import { useMutation } from '@apollo/client';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import {
  DeleteMediaDocument,
  TripMediaQuery,
} from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { MdDelete, MdFileDownload } from 'react-icons/md';
import { Image, getImageUrlForDownload } from '../../../media/utils/imgix';

export type GalleryMedia = TripMediaQuery['trip']['media']['items'][0];

type Props = {
  media: GalleryMedia[];
  mini?: boolean;
};

export default function Gallery({ media, mini = false }: Props) {
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const [deleteMedia] = useMutation(DeleteMediaDocument, {
    update(cache, { data }) {
      const cacheID = cache.identify({
        __typename: 'Media',
        id: data.deleteMedia.id,
      });
      cache.evict({ id: cacheID });
    },
  });

  async function deleteMediaByID(mediaID: string) {
    try {
      const { errors } = await deleteMedia({
        variables: {
          id: mediaID,
        },
      });
      setDeleteErrorMessage(JSON.stringify(errors));
    } catch (error) {
      setDeleteErrorMessage(error.message);
    }
  }

  // Sizes
  const sizes = mini
    ? '(min-width: 1400px) 104px, (min-width: 1200px) 89px, (min-width: 992px) 153px, (min-width: 768px) 113px, (min-width: 576px) 126px, 33vw'
    : '(min-width: 1400px) 213px, (min-width: 1200px) 183px, (min-width: 992px) 231px, (min-width: 768px) 229px, (min-width: 576px) 256px, 50vw';

  return (
    <>
      {/* show error message */}
      {!!deleteErrorMessage && (
        <AlertDismissible
          title="Unable to delete media item"
          message={deleteErrorMessage}
          onClose={() => setDeleteErrorMessage('')}
          variant="danger"
        />
      )}
      <Row className="g-1">
        {media.map((i) => {
          // Get the download link
          const url = `${getImageUrlForDownload(
            i?.file?.key,
          )}&auto=compress,enhance,redeye&crop=faces&w=1920&ar=1:1&fit=crop&fm=jpg&q=90&dl`;

          return (
            <Col
              xl={mini ? 1 : 2}
              lg={mini ? 2 : 3}
              md={mini ? 2 : 4}
              sm={mini ? 3 : 6}
              xs={mini ? 4 : 6}
              key={i.id}
              className="g-1"
              style={{ position: 'relative' }}
            >
              <Image
                imgKey={i.file?.key}
                styles={{
                  width: '100%',
                }}
                sizes={sizes}
                alt={i.id}
                imgixParams={{
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces',
                  fit: 'crop',
                  ar: '1:1',
                }}
              />

              {!mini && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 1000,
                    right: 10,
                    top: 10,
                  }}
                >
                  <Button
                    as="a"
                    href={url}
                    variant="light"
                    aria-label="Download"
                    download
                    style={{
                      background: 'rgba(248, 249, 250,0.6)',
                      border: 'rgba(248, 249, 250, 1)',
                    }}
                  >
                    <MdFileDownload />
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => deleteMediaByID(i.id)}
                    aria-label="Delete"
                    className="ms-2"
                    style={{
                      background: 'rgba(248, 249, 250,0.6)',
                      border: 'rgba(248, 249, 250, 1)',
                    }}
                  >
                    <MdDelete className="text-danger" />
                  </Button>
                </div>
              )}
            </Col>
          );
        })}
      </Row>
    </>
  );
}
